import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue";
import { RunInfoCard } from "@src/components/RunDetails/RunInfoCard";
import { RunBlock } from "@src/components/RunResults/RunBlock";
import { RunDetailProvider } from "@src/contexts/RunDetailContext";
import { useToastActions } from "@src/hooks/useToasts";
import { useValueWhileMounted } from "@src/hooks/useValueWhileMounted";
import { tdApi } from "@src/services/api/tdApi";
import {
  GroupMembershipChangeAbandonedError,
  runGroupMembershipChange,
} from "@src/services/api/groupMembership";
import type * as T from "@src/types";
import type {
  ApiExecution,
  ApiExecutionGroup,
  ApiPosition,
  BacktestMetrics,
} from "@tradingdroid/frontends-types";
import { formatDateTimeShort } from "@src/utils/dateUtils";

import { hasOnlyTrackingEndedPositions, isPositionTrackingEnded } from "@src/utils/positionStatus";
import styles from "./ExecutionGroupDetailPage.module.scss";

export const getProfit = (p: ApiPosition): number | null =>
  isPositionTrackingEnded(p) ? null : (p.net_profit_usdt ?? p.net_profit_quote ?? 0);

export function computeExecStats(positions: ApiPosition[]) {
  const included = positions.filter((p) => !isPositionTrackingEnded(p));
  const excludedPositions = positions.length - included.length;
  const profits = included.map((p) => getProfit(p)!);
  const wins = profits.filter((v) => v > 0);
  const losses = profits.filter((v) => v < 0);
  const pnl = profits.reduce((s, v) => s + v, 0);
  return {
    totalPositions: included.length,
    excludedPositions,
    wins: wins.length,
    losses: losses.length,
    avgWin: wins.length ? wins.reduce((s, v) => s + v, 0) / wins.length : 0,
    avgLoss: losses.length ? losses.reduce((s, v) => s + v, 0) / losses.length : 0,
    pnl: hasOnlyTrackingEndedPositions(positions) ? null : pnl,
  };
}

/** Build a synthetic ApiExecution that merges all group executions into one run */
function buildSyntheticRun(
  groupData: ApiExecutionGroup,
  executions: ApiExecution[],
  metrics: BacktestMetrics | null,
): ApiExecution {
  const firstExec = executions[0];
  const lastExec = executions[executions.length - 1];
  const allPositions = executions.flatMap((ex) => ex.positions || []);

  return {
    ...firstExec,
    id: groupData.id,
    name: groupData.name,
    start_ts: firstExec.start_ts,
    end_ts: lastExec.end_ts || undefined,
    positions: allPositions,
    metrics: metrics ?? undefined,
    cache_url: groupData.cache_url ?? undefined,
    cache_ts: groupData.cache_ts ?? undefined,
    cache_chart_definition: groupData.cache_chart_definition,
    tags: groupData.tags,
    execution_group_id: groupData.id,
    _is_group_synthetic: true,
    _group_execution_ids: executions.map((ex) => ex.id),
  } as ApiExecution;
}

export const ungroupExecution = async (
  groupId: number,
  executionId: number,
  executionCount: number,
  onWaitingForGroupCache?: () => void,
  isStillWanted?: () => boolean,
): Promise<"dissolved" | "updated"> => {
  // Invalidate the group cache so metrics recalculate (best-effort): a refused invalidation leaves
  // the cached metrics describing the old membership, and it must not stop the ungroup below. The
  // dissolving path deletes the group outright, which drops the cached values with the row anyway.
  await tdApi.patchExecutionGroup(groupId, { cache_url: null, cache_ts: null }).catch(() => {});

  // A group of two or fewer dissolves here, and deleting it already sets every member's
  // execution_group_id to null. Detaching the selected execution first is therefore redundant, and
  // it costs the caller the membership the group write is authorized by: once their own execution
  // is out, a group whose remaining member belongs to someone else refuses the delete.
  if (executionCount > 2) {
    await runGroupMembershipChange(
      () => tdApi.patchExecution(executionId, { execution_group_id: null }),
      [groupId],
      onWaitingForGroupCache,
      isStillWanted,
    );
    return "updated";
  }

  await tdApi.deleteExecutionGroup(groupId);
  return "dissolved";
};

interface Props {}

export const ExecutionGroupDetailPage: React.FC<Props> = () => {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToastActions();
  const navigate = useNavigate();

  const [group, setGroup] = useState<ApiExecutionGroup | null>(null);
  const [mergedRun, setMergedRun] = useState<T.ApiRun | null>(null);
  const [boundaries, setBoundaries] = useState<T.ExecutionBoundary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupView, setGroupView] = useState(() => ({
    id,
    groupId: id ? parseInt(id, 10) : undefined,
  }));
  if (groupView.id !== id) {
    setGroupView({ id, groupId: id ? parseInt(id, 10) : undefined });
  }
  const activeGroupView = useValueWhileMounted(groupView);
  const [pendingUngroup, setPendingUngroup] = useState<{
    operation: { view: typeof groupView; groupId: number; executionId: number };
    waitingForCache: boolean;
  } | null>(null);
  const displayedUngroup =
    pendingUngroup?.operation.view === groupView && pendingUngroup.operation.groupId === group?.id
      ? pendingUngroup
      : null;

  useEffect(() => {
    if (!id) {
      setError("Missing group id in URL");
      setLoading(false);
      return;
    }

    const numId = parseInt(id, 10);
    if (Number.isNaN(numId)) {
      setError("Invalid group id");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const loadGroup = async () => {
      try {
        // Fetch group + live metrics in parallel
        const [groupData, liveMetrics] = await Promise.all([
          tdApi.getExecutionGroup(numId, {
            embed: "executions(positions(movements(order)))",
          }),
          tdApi.getExecutionGroupMetrics(numId),
        ]);

        if (!groupData) {
          showToast({ message: `Execution group ${id} not found`, type: "danger" });
          navigate("/executions");
          return;
        }

        setGroup(groupData);

        // Sort executions by start_ts
        const executions = (groupData.executions || []).sort(
          (a, b) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime(),
        );

        if (executions.length > 0) {
          const synthetic = buildSyntheticRun(
            groupData,
            executions,
            (liveMetrics as BacktestMetrics) ?? null,
          );
          setMergedRun(synthetic);

          setBoundaries(
            executions.map((ex) => ({
              executionId: ex.id,
              startTime: Math.floor(new Date(ex.start_ts).getTime() / 1000),
              endTime: ex.end_ts ? Math.floor(new Date(ex.end_ts).getTime() / 1000) : undefined,
              startTs: ex.start_ts,
              endTs: ex.end_ts ?? null,
            })),
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        showToast({ message: `Failed to load group: ${message}`, type: "danger" });
      } finally {
        setLoading(false);
      }
    };

    loadGroup();
  }, [id, showToast, navigate]);

  // Rebuild mergedRun and boundaries from current group state, fetching live metrics
  const rebuildFromExecutions = useCallback(
    async (groupData: ApiExecutionGroup, execs: ApiExecution[]) => {
      const view = activeGroupView.current;
      if (!view || view.groupId !== groupData.id) return;
      if (execs.length === 0) {
        setMergedRun(null);
        setBoundaries([]);
        return;
      }
      const sorted = [...execs].sort(
        (a, b) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime(),
      );

      const liveMetrics = await tdApi.getExecutionGroupMetrics(groupData.id);
      if (activeGroupView.current !== view || view.groupId !== groupData.id) return;
      const synthetic = buildSyntheticRun(
        groupData,
        sorted,
        (liveMetrics as BacktestMetrics) ?? null,
      );
      setMergedRun(synthetic);
      setBoundaries(
        sorted.map((ex) => ({
          executionId: ex.id,
          startTime: Math.floor(new Date(ex.start_ts).getTime() / 1000),
          endTime: ex.end_ts ? Math.floor(new Date(ex.end_ts).getTime() / 1000) : undefined,
          startTs: ex.start_ts,
          endTs: ex.end_ts ?? null,
        })),
      );
    },
    [activeGroupView],
  );

  const handleUngroup = useCallback(
    async (execId: number) => {
      if (!group) return;
      const view = activeGroupView.current;
      if (!view || view.groupId !== group.id) return;
      const operation = { view, groupId: group.id, executionId: execId };
      const isStillWanted = () => activeGroupView.current === view && view.groupId === group.id;
      const currentExecs = group.executions || [];

      if (currentExecs.length <= 2) {
        if (
          !window.confirm(
            "This group has 2 or fewer executions. Ungrouping will dissolve the group.",
          )
        ) {
          return;
        }
      }

      setPendingUngroup((pending) =>
        isStillWanted() && pending?.operation.view !== view
          ? { operation, waitingForCache: false }
          : pending,
      );
      try {
        const outcome = await ungroupExecution(
          group.id,
          execId,
          currentExecs.length,
          () =>
            setPendingUngroup((pending) =>
              pending?.operation === operation ? { ...pending, waitingForCache: true } : pending,
            ),
          isStillWanted,
        );
        const remaining = currentExecs.filter((ex) => ex.id !== execId);

        if (outcome === "dissolved") {
          showToast({ message: "Group dissolved", type: "info" });
          navigate("/executions");
          return;
        }

        // Update local state after the backend invalidates the cache and removes the execution
        const updatedGroup = {
          ...group,
          executions: remaining,
          cache_url: undefined,
          cache_ts: undefined,
        };
        if (isStillWanted()) {
          setGroup(updatedGroup);
          await rebuildFromExecutions(updatedGroup, remaining);
        }
        showToast({ message: `Execution #${execId} removed from group`, type: "success" });
      } catch (err) {
        if (err instanceof GroupMembershipChangeAbandonedError) return;
        const msg = err instanceof Error ? err.message : String(err);
        showToast({ message: `Failed to ungroup: ${msg}`, type: "danger" });
      } finally {
        setPendingUngroup((pending) => (pending?.operation === operation ? null : pending));
      }
    },
    [group, showToast, navigate, rebuildFromExecutions, activeGroupView],
  );

  // Hooks must be called unconditionally (before any early returns)
  const noopUpdateRun = useCallback(() => {}, []);
  const contextValue = useMemo(
    () => ({ run: mergedRun, type: "execution" as const, updateRun: noopUpdateRun }),
    [mergedRun, noopUpdateRun],
  );

  if (loading) {
    return (
      <CCard>
        <CCardBody className="text-center p-4">
          <CSpinner />
          <p className="mt-2">Loading execution group...</p>
        </CCardBody>
      </CCard>
    );
  }

  if (error) {
    return (
      <CCard>
        <CCardBody className="text-center p-4">
          <div className="text-danger mb-3">
            <strong>Error Loading Group</strong>
          </div>
          <p>{error}</p>
        </CCardBody>
      </CCard>
    );
  }

  if (!group) return null;

  const executions = (group.executions || []).sort(
    (a, b) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime(),
  );

  return (
    <RunDetailProvider value={contextValue}>
      <div className="ExecutionGroupDetailPage runType-execution">
        {/* Reuse RunInfoCard for the standard summary layout */}
        <RunInfoCard run={mergedRun} type="execution" headerAction={<></>} />

        {/* Executions breakdown */}
        {executions.length > 0 && (
          <CCard className="ExecutionGroupBreakdown mb-3">
            <CCardBody className="p-2">
              {group.description && (
                <p className="groupDescription text-body-secondary small mb-2 px-2">
                  {group.description}
                </p>
              )}

              {/* Responsive table - horizontal scroll on mobile */}
              <CTable small hover borderless responsive className="executionGroupTable mb-0 small">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className="col-execution">Execution</CTableHeaderCell>
                    <CTableHeaderCell className="col-start">Start</CTableHeaderCell>
                    <CTableHeaderCell className="col-end">End</CTableHeaderCell>
                    <CTableHeaderCell className="col-days text-end">Days</CTableHeaderCell>
                    <CTableHeaderCell className="col-pos text-end">Pos</CTableHeaderCell>
                    <CTableHeaderCell className="col-wins text-end">W</CTableHeaderCell>
                    <CTableHeaderCell className="col-losses text-end">L</CTableHeaderCell>
                    <CTableHeaderCell className="col-avgWin text-end">Avg W</CTableHeaderCell>
                    <CTableHeaderCell className="col-avgLoss text-end">Avg L</CTableHeaderCell>
                    <CTableHeaderCell className="col-pnl text-end">PnL</CTableHeaderCell>
                    <CTableHeaderCell className="col-pnlPct text-end">PnL%</CTableHeaderCell>
                    <CTableHeaderCell className="col-twr text-end">TWR%</CTableHeaderCell>
                    <CTableHeaderCell className="col-status text-end">Status</CTableHeaderCell>
                    <CTableHeaderCell className="col-actions" />
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {executions.map((ex) => {
                    const positions = ex.positions || [];
                    const stats = computeExecStats(positions);
                    const execCapital = ex.config?.portfolio?.initial_capital ?? 0;
                    const pnlPct =
                      stats.pnl != null && execCapital > 0 ? (stats.pnl / execCapital) * 100 : null;
                    const endDate = ex.end_ts ? new Date(ex.end_ts) : new Date();
                    const days = Math.floor(
                      (endDate.getTime() - new Date(ex.start_ts).getTime()) / 86_400_000,
                    );

                    return (
                      <CTableRow key={ex.id}>
                        <CTableDataCell>
                          <Link to={`/execution/${ex.id}`}>#{ex.id}</Link>
                        </CTableDataCell>
                        <CTableDataCell>{formatDateTimeShort(ex.start_ts)}</CTableDataCell>
                        <CTableDataCell>
                          {ex.end_ts ? formatDateTimeShort(ex.end_ts) : "-"}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">{days}d</CTableDataCell>
                        <CTableDataCell className="text-end">
                          {stats.totalPositions}
                          {stats.excludedPositions > 0 && (
                            <small
                              className={`MemberExcludedCount ${styles.MemberExcludedCount}`}
                              title="Incomplete accounting: tracking-ended positions are excluded from W, L, PnL and PnL%"
                            >
                              +{stats.excludedPositions} tracking ended
                            </small>
                          )}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <ColoredValue value={stats.wins} forceColor="win" type="flat" />
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <ColoredValue value={stats.losses} forceColor="loss" type="flat" />
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          {stats.avgWin > 0 ? (
                            <ColoredValue
                              value={stats.avgWin}
                              type="currency"
                              decimals={2}
                              forceColor="win"
                            />
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          {stats.avgLoss < 0 ? (
                            <ColoredValue
                              value={stats.avgLoss}
                              type="currency"
                              decimals={2}
                              forceColor="loss"
                            />
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          {stats.pnl == null ? (
                            <span className={`MemberPnlUnknown ${styles.MemberPnlUnknown}`}>
                              Unknown
                            </span>
                          ) : stats.pnl !== 0 ? (
                            <ColoredValue
                              value={stats.pnl}
                              threshold={0}
                              type="currency"
                              decimals={2}
                            />
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          {stats.pnl == null ? (
                            <span className={`MemberPnlUnknown ${styles.MemberPnlUnknown}`}>
                              Unknown
                            </span>
                          ) : pnlPct != null ? (
                            <ColoredValue
                              value={pnlPct}
                              threshold={0}
                              type="percentage"
                              decimals={2}
                            />
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          {ex.metrics?.cumulative_returns_pct != null ? (
                            <ColoredValue
                              value={ex.metrics.cumulative_returns_pct}
                              threshold={0}
                              type="percentage"
                              decimals={2}
                            />
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <CBadge
                            color={
                              ex.status_name === "running"
                                ? "success"
                                : ex.status_name === "stopped"
                                  ? "secondary"
                                  : "warning"
                            }
                            size="sm"
                          >
                            {ex.status_name || "unknown"}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <CButton
                            color="danger"
                            variant="ghost"
                            size="sm"
                            disabled={displayedUngroup !== null}
                            onClick={() => handleUngroup(ex.id)}
                          >
                            {displayedUngroup?.operation.executionId === ex.id ? (
                              <>
                                <CSpinner size="sm" />
                                {displayedUngroup.waitingForCache && (
                                  <span className="ungroupWaitingLabel ms-1">
                                    Waiting for group cache…
                                  </span>
                                )}
                              </>
                            ) : (
                              "Ungroup"
                            )}
                          </CButton>
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        )}

        {/* Chart + Positions + Performance (reusing RunBlock) */}
        {mergedRun && mergedRun.positions && (
          <RunBlock run={mergedRun} type="execution" executionBoundaries={boundaries} />
        )}
      </div>
    </RunDetailProvider>
  );
};
